import { Injectable } from '@angular/core';

import { cloneDeep, get, set, unset } from 'lodash-es';
import * as dayjs from 'dayjs';

import { FormlyService } from './formly.service';

import { IAudit, IAuditSection, IAuditSectionQuestionStatus, IAuditSectionStatus, IFormConfig, IFormConfigField, IFormConfigSection, ILocationPosition } from '@models/types';

@Injectable({
	providedIn: 'root'
})
export class AuditService {

	private submittedAudit: IAudit;

	constructor(
		private formlyService: FormlyService,
	) { }

	public getSubmittedAudit(): IAudit {
		return this.submittedAudit;
	}

	public async initialiseAudit(formConfig: IFormConfig) {
		try {
			this.formlyService.reset();
			const refactoredFormConfigDocument = this.refactorFormConfig(formConfig);
			this.formlyService.setFormConfig(refactoredFormConfigDocument);
			const auditDocument = await this.createAudit(refactoredFormConfigDocument);
			this.formlyService.setModel(auditDocument);
			this.updateModel();
			this.saveAudit();
		} catch (error) {
			throw error;
		}
	}

	private refactorFormConfig(formConfig: IFormConfig) {
		for (const section of formConfig.sections) {
			for (const question of section.questions) {
				for (const field of question.fields) {
					if (field.hideExpression) {
						const newHideExpression = field.hideExpression.replace(/formState\./g, '').replace(/\./g, '\?.');
						field.hideExpression = newHideExpression;
					}

					if (field.templateOptions) {
						if (field.templateOptions.options) {
							field.templateOptions.labelProp = "value";
							field.templateOptions.valueProp = "key";
						}

						field.props = { ...field.templateOptions };
						delete field.templateOptions;
					}
				}
			}
		}

		return formConfig;
	}

	private async createAudit(formConfig: IFormConfig) {
		try {
			const uuid = crypto.randomUUID().replace(/-/g, '');
			const auditId = 'audit::' + uuid + '::v1';

			const auditDocument: IAudit = {
				data: {},
				api: formConfig.api,
				channels: [],
				description: formConfig.description,
				failures: {
					total: 0,
					category_a_count: 0,
					category_b_count: 0,
					category_c_count: 0
				},
				form_config_id: formConfig.id,
				id: auditId,
				lifecycle: {
					started_timestamp: dayjs().toISOString(),
					last_updated_timestamp: dayjs().toISOString(),
					status: 'incomplete'
				},
				intl: {
					locale: navigator.language,
					timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
				},
				title: formConfig.title,
				type: 'audit',
				user_id: '',
				user_name: '',
				uuid,
				version: 1
			};

			/*
			 * Create options defaults for checkbox question types, this enables the checkbox to
			 * be a subquestion of radio and pass-fail
			 */
			const dataModel = {
				data: {}
			};

			for (const formConfigSection of formConfig.sections) {
				for (const formConfigQuestion of formConfigSection.questions) {
					if (formConfigQuestion.fields) {
						for (const field of formConfigQuestion.fields) {
							/*
							 * We need to set the default value of the checkbox to false to ensure the hide expression works
							 */
							const key = field.key;
							const path = key.substring(0, key.lastIndexOf('.')) + '.value';
							if (field.props.options) {
								if (field.type === 'ta-radio') {
									set(dataModel, path, null);
								}
							}
						}
					}
				}
			}

			auditDocument.data = dataModel.data;
			return auditDocument;
		} catch (error) {
			throw error;
		}
	}

	private updateModel() {
		const sections = this.formlyService.getFormConfig().sections;
		let sectionIndex = 0;

		for (const section of sections) {
			this.checkFormConfigForRepeatSections(section);
			this.updateAuditSectionStatus(section, sectionIndex, section.repeat);
			this.formlyService.attachValidators(section);
			sectionIndex++;
		}
	}

	private checkFormConfigForRepeatSections(section: IFormConfigSection) {
		if (section.repeat) {
			section.repeat_sections = [];
			if (this.formlyService.getModel().data[section._id]) {
				/*
				 * The data model does not store the keys in any given order. We know that when adding new repeat
				 * section items generate a new section id by adding 1 to the numerical part of last section id.
				 *
				 * For example, if the last section in repeat sections has an _id of section_14 then the next repeat
				 * section id will be section_15.
				 *
				 * This means that within the repeat sections of the form_config the _id's are always numerically sorted
				 * in ascending order. We can use this numerical information to ensure that we push the correct model data
				 * for the key into the right slot in the repeat_sections array.
				 *
				 * Create the sorted list of keys and use this sorted list to push the form_config repeat_sections
				 */
				const keys: string[] = [];
				const repeatSections = (this.formlyService.getModel().data[section._id].repeat_sections as IFormConfigSection[]);
				for (const key in repeatSections) {
					if (repeatSections.hasOwnProperty(key)) {
						keys.push(key);
					}
				}

				const sortedKeys = keys.sort((a, b) => {
					const aItemNumber = Number(a.substring(a.lastIndexOf('_') + 1));
					const bItemNumber = Number(b.substring(b.lastIndexOf('_') + 1));
					if (bItemNumber > aItemNumber) {
						return -1;
					} else if (bItemNumber === aItemNumber) {
						return 0;
					} else {
						return 1;
					}
				});

				for (const key of sortedKeys) {
					const repeatSection = this.generateRepeatSectionItem(section, key);
					section.repeat_sections.push(repeatSection);
				}
			}
		}
	}

	public generateRepeatSectionItem(section: IFormConfigSection, key?: string) {
		const newSectionItem = cloneDeep(section);
		delete newSectionItem.repeat_sections;
		delete newSectionItem.repeat;

		if (key) {
			newSectionItem._id = key;
		} else {
			let sectionItemNumber = 0;
			if (section.repeat_sections.length > 0) {
				const lastSectionItem = section.repeat_sections[section.repeat_sections.length - 1];
				let lastSectionNumber: number = parseInt(lastSectionItem._id.substring(lastSectionItem._id.lastIndexOf('_') + 1), 10);
				sectionItemNumber = (lastSectionNumber = +lastSectionNumber + 1);
			}
			newSectionItem._id = 'section_' + sectionItemNumber;
		}
		newSectionItem.parent_section = section._id;

		for (const question of newSectionItem.questions) {
			for (const field of question.fields) {
				if (field.hideExpression) {
					const hideExpression = (field.hideExpression).toString();
					const changedHideExpression = hideExpression.replace(new RegExp(section._id, 'g'), section._id + '.repeat_sections.' + newSectionItem._id);
					field.hideExpression = changedHideExpression as string;
				}
				const originalKey = field.key;
				field.key = field.key.replace(new RegExp(section._id, 'g'), section._id + '.repeat_sections.' + newSectionItem._id);

				const path = field.key.substring(0, field.key.lastIndexOf('.')) + '.value';

				if (field.props.options) {
					if (field.type === 'ta-radio') {
						const currentValue = get(this.formlyService.getModel(), field.key);
						set(this.formlyService.getModel(), path, currentValue);
					}
				}
			}
		}
		// Only attach validators when adding a new section item
		if (!key) {
			this.formlyService.setQuestionValidators(newSectionItem);
		}
		return newSectionItem;
	}

	public setLocationAndTimestamp(key: string | number | string[]) {
		const path = (key as string).substring(0, (key as string).lastIndexOf('.'));
		const timeStamp = dayjs().toISOString();
		set(this.formlyService.getModel(), path + '.timestamp', timeStamp);
		set(this.formlyService.getModel(), 'lifecycle.last_updated_timestamp', timeStamp);
	}

	public unsetLocationAndTimestamp(key: string | number | string[]) {
		const path = (key as string).substr(0, (key as string).lastIndexOf('.'));
		unset(this.formlyService.getModel(), path + '.timestamp');
		unset(this.formlyService.getModel(), path + '.location');
	}

	public updateAuditSectionStatus(section: IFormConfigSection, sectionIndex: number, isRepeatSection: boolean) {
		if (isRepeatSection) {
			this.formlyService.getStatus().sections[sectionIndex] = this.getRepeatAuditSectionItemStatuses(section);
		} else {
			this.formlyService.getStatus().sections[sectionIndex] = this.getAuditSectionStatus(section);
		}
		this.formlyService.getStatus().form_valid = this.calculateAuditFormValidity();
	}

	public getRepeatAuditSectionItemStatuses(parentSection: IFormConfigSection): IAuditSectionStatus {
		const repeatSectionStatuses: IAuditSectionStatus[] = [];
		for (const repeatSection of parentSection.repeat_sections) {
			const sectionStatus = this.getAuditSectionStatus(repeatSection);
			repeatSectionStatuses.push(sectionStatus);
		}

		let numberOfSectionItems = 0;
		let numberOfSectionItemRequired = 0;
		let numberOfSectionItemsAnswered = 0;
		let sectionValid = true;

		for (const repeatSectionStatus of repeatSectionStatuses) {
			if (repeatSectionStatus.valid) {
				numberOfSectionItemsAnswered++;
			} else {
				numberOfSectionItemRequired++;
				if (sectionValid) {
					sectionValid = false;
				}
			}
			numberOfSectionItems++;
		}

		return {
			number_of_section_items: numberOfSectionItems,
			number_of_section_items_required: numberOfSectionItemRequired,
			number_of_section_items_answered: numberOfSectionItemsAnswered,
			repeat_sections: repeatSectionStatuses,
			display_text: this.getRepeatSectionDisplaytext(numberOfSectionItems, numberOfSectionItemRequired),
			valid: sectionValid
		};
	}

	public getRepeatSectionQuestionStatuses(section: IFormConfigSection): Array<IAuditSectionQuestionStatus[]> {
		const repeatSectionQuestionStatuses: Array<IAuditSectionQuestionStatus[]> = [];
		const repeatSections = section.repeat_sections;
		for (const repeatSection of repeatSections) {
			repeatSectionQuestionStatuses.push(this.getRepeatSectionQuestionStatus(repeatSection));
		}
		return repeatSectionQuestionStatuses;
	}

	public getRepeatSectionQuestionStatus(section: IFormConfigSection): IAuditSectionQuestionStatus[] {
		const repeatSectionQuestionStatus: IAuditSectionQuestionStatus[] = [];
		for (const question of section.questions) {
			for (const field of question.fields) {
				if (field.props.mobileItemList) {
					const value = get(this.formlyService.getModel(), field.key, null);
					if (value || ((field.type === 'ta-number' || field.type === 'ta-decimal' || field.type === 'ta-range') && typeof value === 'number')) {
						if (field.type === 'ta-radio') {
							const options = field.props.options;
							repeatSectionQuestionStatus.push({
								label: field.props.label, answer: options.find((o: any) => o.key === value).value
							});
						} else if (field.type === 'ta-checkbox') {
							const options: any[] = field.props.options;
							const answers: string[] = [];
							for (const option of options) {
								if (value[option.key]) {
									answers.push(option.value);
								}
							}
							repeatSectionQuestionStatus.push({
								label: field.props.label, answer: answers.join(', ')
							});
						} else {
							repeatSectionQuestionStatus.push({ label: field.props.label, answer: value });
						}
					}
				}
			}
		}
		return repeatSectionQuestionStatus;
	}

	private getAuditSectionStatus(section: IFormConfigSection): IAuditSectionStatus {
		let numberOfRequiredQuestionsAnswered = 0;
		let numberOfRequiredQuestionsUnanswered = 0;
		let numberOfOptionalQuestionsAnswered = 0;
		let numberOfOptionalQuestionsUnanswered = 0;
		let numberOfOptionalQuestionsLeftOut = 0;

		const getQuestionAnswer = (field: IFormConfigField) => get(this.formlyService.getModel(), field.key, null);

		const updateQuestionAnsweredCount = (field: IFormConfigField,) => {
			const answer = getQuestionAnswer(field);
			let answeredCount = 0;
			let unansweredCount = 0;

			if (field.type === 'ta-number' || field.type === 'ta-decimal' || field.type === 'ta-range') {
				if (!isNaN(parseFloat(answer))) {
					answeredCount++;
				} else {
					unansweredCount++;
				}
			} else if (field.type === 'ta-checkbox') {
				if (answer && Object.values(answer).some(v => v)) {
					answeredCount++;
				} else {
					unansweredCount++;
				}
			} else {
				if (answer) {
					answeredCount++;
				} else {
					unansweredCount++;
				}
			}
			return { answeredCount, unansweredCount };
		};

		for (const question of section.questions) {
			const questionsWithHideExpressions = question.fields.filter(nq => (nq.hideExpression));
			for (const field of question.fields) {
				let additionalFieldRequiredQuestionLeftOut = false;
				let numberOfAdditionalFieldRequiredQuestionLeftOut = 0;

				if (!field.hideExpression && field.type !== 'ta-hidden') {

					const checkQuestionValidation = (questionField: IFormConfigField) => {
						const validators = questionField.ta_validators;
						if (validators.required || validators.checkboxRequired) {
							const { answeredCount, unansweredCount } = updateQuestionAnsweredCount(field);
							numberOfRequiredQuestionsUnanswered += unansweredCount;
							numberOfRequiredQuestionsAnswered += answeredCount;

							if (additionalFieldRequiredQuestionLeftOut) {
								if (answeredCount > 0) {
									numberOfRequiredQuestionsAnswered--;
									numberOfRequiredQuestionsUnanswered++;
								}
							}
						} else {
							const { answeredCount, unansweredCount } = updateQuestionAnsweredCount(field);
							numberOfOptionalQuestionsUnanswered += unansweredCount;
							numberOfOptionalQuestionsAnswered += answeredCount;

							if (additionalFieldRequiredQuestionLeftOut) {
								if (answeredCount > 0) {
									numberOfOptionalQuestionsAnswered--;
								}
								numberOfRequiredQuestionsUnanswered++;
							}
						}
					};

					const checkNestedQuestionFields = (questionField: IFormConfigField, length: number) => {
						const checkNestedQuestionField = (answer: any) => {
							if (answer || ((questionField.type === 'ta-number' || questionField.type === 'ta-decimal' || questionField.type === 'ta-range') && !isNaN(parseFloat(answer)))) {
								const response = answer.replace('option_', 'response_');
								const path = questionField.key.split('.value')[0];
								const partialNestedKey = path + '.' + response + '.nested_question';
								const nestedQuestionFields = questionsWithHideExpressions.filter(n => n.key.includes(partialNestedKey) && (n.key.match(/nested_question/g) || []).length === length);
								if (nestedQuestionFields.length > 0) {
									for (const nestedQuestionField of nestedQuestionFields) {
										const validators = nestedQuestionField.ta_validators;
										const nestedQuestionAnswer = getQuestionAnswer(nestedQuestionField);
										additionalFieldRequiredQuestionLeftOut = false;
										if (validators.required || validators.checkboxRequired) {
											if (additionalFieldRequiredQuestionLeftOut && numberOfAdditionalFieldRequiredQuestionLeftOut === 1) {
												numberOfRequiredQuestionsAnswered--;
												numberOfRequiredQuestionsUnanswered++;
												break;
											} else {
												if (nestedQuestionAnswer || ((nestedQuestionField.type === 'ta-number' || nestedQuestionField.type === 'ta-decimal' || nestedQuestionField.type === 'ta-range') && !isNaN(parseFloat(nestedQuestionAnswer)))) {
													if (nestedQuestionField.type === 'ta-checkbox') {
														if (!(Object.values(nestedQuestionAnswer).some(v => v))) {
															numberOfRequiredQuestionsAnswered--;
															numberOfRequiredQuestionsUnanswered++;
															break;
														}
													} else if (nestedQuestionField.type === 'ta-number' || nestedQuestionField.type === 'ta-decimal' || nestedQuestionField.type === 'ta-range') {
														if (isNaN(parseFloat(nestedQuestionAnswer))) {
															numberOfRequiredQuestionsAnswered--;
															numberOfRequiredQuestionsUnanswered++;
															break;
														}
													}
												} else {
													numberOfRequiredQuestionsAnswered--;
													numberOfRequiredQuestionsUnanswered++;
													break;
												}
											}
										} else {
											if (additionalFieldRequiredQuestionLeftOut && numberOfAdditionalFieldRequiredQuestionLeftOut === 1) {
												numberOfOptionalQuestionsAnswered--;
												numberOfRequiredQuestionsUnanswered++;
											} else {
												if (nestedQuestionAnswer || ((nestedQuestionField.type === 'ta-number' || nestedQuestionField.type === 'ta-decimal' || nestedQuestionField.type === 'ta-range') && !isNaN(parseFloat(nestedQuestionAnswer)))) {
													if (nestedQuestionField.type === 'ta-checkbox') {
														if (!(Object.values(nestedQuestionAnswer).some(v => v))) {
															numberOfOptionalQuestionsLeftOut++;
														}
													} else if (nestedQuestionField.type === 'ta-number' || nestedQuestionField.type === 'ta-decimal' || nestedQuestionField.type === 'ta-range') {
														if (isNaN(parseFloat(nestedQuestionAnswer))) {
															numberOfRequiredQuestionsAnswered--;
															numberOfRequiredQuestionsUnanswered++;
														}
													}
												} else {
													numberOfOptionalQuestionsLeftOut++;
												}
											}
										}
										checkNestedQuestionFields(nestedQuestionField, length + 1);
									}
								}
							}
						};
						const questionAnswer = getQuestionAnswer(questionField);
						if (questionField.type === 'ta-radio') {
							checkNestedQuestionField(questionAnswer);
						} else if (questionField.type === 'ta-checkbox') {
							if (questionAnswer) {
								const checkboxAnswers = Object.keys(questionAnswer).filter(v => questionAnswer[v]);
								for (const checkboxAnswer of checkboxAnswers) {
									checkNestedQuestionField(checkboxAnswer);
								}
							}
						}
					};

					checkQuestionValidation(field);

					if (questionsWithHideExpressions.length > 0) {
						checkNestedQuestionFields(field, 1);
					}

				}
			}
		}

		return {
			total_questions: section.questions.length,
			number_of_questions_answered: numberOfOptionalQuestionsAnswered + numberOfRequiredQuestionsAnswered,
			number_of_required_questions_unanswered: numberOfRequiredQuestionsUnanswered,
			number_of_optional_questions_unanswered: numberOfOptionalQuestionsUnanswered,
			number_of_optional_questions_left_out: numberOfOptionalQuestionsLeftOut,
			display_text: this.getSectionDisplaytext(numberOfRequiredQuestionsUnanswered, numberOfOptionalQuestionsUnanswered, numberOfOptionalQuestionsLeftOut),
			valid: numberOfRequiredQuestionsUnanswered === 0
		};
	}

	private getRepeatSectionDisplaytext(numberOfSectionItems: number, numberOfSectionItemRequired: number): string {
		if (numberOfSectionItems === 0) {
			return 'No items';
		} else if (numberOfSectionItemRequired === 0) {
			return 'All items are completed';
		} else {
			if (numberOfSectionItemRequired > 0 && numberOfSectionItemRequired < 2) {
				// If 1 item is remaining
				return numberOfSectionItemRequired + ' item is yet to be completed';
			} else {
				// If more than 1 items are remaining
				return numberOfSectionItemRequired + ' items are yet to be completed';
			}
		}
	}

	private getSectionDisplaytext(numberOfRequiredQuestionsUnanswered: number, numberOfOptionalQuestionsUnanswered: number, numberOfOptionalQuestionsLeftOut: number): string {
		if (numberOfRequiredQuestionsUnanswered > 0) {
			return numberOfRequiredQuestionsUnanswered + ' required question' + (numberOfRequiredQuestionsUnanswered > 1 ? 's' : '') + ' remaining';
		} else if (numberOfOptionalQuestionsUnanswered > 0 || numberOfOptionalQuestionsLeftOut > 0) {
			return 'All mandatory questions answered';
		} else {
			return 'All questions answered';
		}
	}

	private calculateAuditFormValidity(): boolean {
		for (const sectionStatus of this.formlyService.getStatus().sections) {
			if (sectionStatus.number_of_required_questions_unanswered > 0 || sectionStatus.number_of_section_items_required > 0) {
				return false;
			}
		}
		return true;
	}

	public async updateAuditForSubmission() {
		/*
		 * Create a clone of the audit passed in to prevent the original audit from being edited which could
		 * lead to formly errors
		 */
		const audit = cloneDeep(this.formlyService.getModel());
		const formConfig = this.formlyService.getFormConfig();

		// Update lifecycle for completion
		audit.lifecycle.status = 'completed';
		audit.lifecycle.completed_timestamp = dayjs().toISOString();

		// Audit failure status
		audit.failures = {
			total: 0,
			category_a_count: 0,
			category_b_count: 0,
			category_c_count: 0
		};

		// Create sections array
		audit.sections = [];
		audit.scores = [];

		const updateQuestions = async (formConfigSection: IFormConfigSection, section: IAuditSection, repeatSectionIndex?: number) => {
			const incrementFailureCount = (failedValue: string) => {
				// Increment failure total of audit and current section
				if (failedValue === 'A' || failedValue === 'B' || failedValue === 'C') {
					audit.failures.total++;
					section.failures.total++;
				}
				// Increment category failure counts
				if (failedValue === 'A') {
					audit.failures.category_a_count++;
					section.failures.category_a_count++;
				} else if (failedValue === 'B') {
					audit.failures.category_b_count++;
					section.failures.category_b_count++;
				} else if (failedValue === 'C') {
					audit.failures.category_c_count++;
					section.failures.category_c_count++;
				}
			};

			const generateQuestionField = async (field: IFormConfigField, parentFieldType?: string, repeatQuestionNumber?: number) => {
				const question: any = {};
				const fieldKeyWithoutValueSuffix = field.key.substring(0, field.key.lastIndexOf('value'));
				question.questionNumber = repeatQuestionNumber ? repeatQuestionNumber : questionNumber;
				question.key = field.key;
				question.type = field.type;
				question.timestamp = get(audit, fieldKeyWithoutValueSuffix + 'timestamp', null);
				question.location = get(audit, fieldKeyWithoutValueSuffix + 'location', null);
				question.nestLevel = (field.key.match(/\.findings\./g) || []).length;
				question.label = field.type === 'ta-repeat-question' ? field.props.addText : field.props.label ? field.props.label : '';
				question.guidance = field.props.guidance;
				question.options = field.props.options ? field.props.options : null;
				question.answer = get(audit, field.key, null);
				question.uuid = field.uuid;

				if ((question.type === 'ta-radio') && typeof question.answer !== 'string') {
					question.answer = null;
				}

				question.required = false;
				if (field.ta_validators) {
					if (field.ta_validators.required) {
						question.required = true;
					}
				}
				question.failed = get(audit, fieldKeyWithoutValueSuffix + 'failed', null);

				if (question.type === 'ta-checkbox') {
					// Checkbox type questions can have multiple failures so must loop through all the options
					for (const option of field.props.options) {
						if (option.failedResponse) {
							if (option.failedIfChecked) {
								if (question.answer && question.answer[option.key] === true) {
									const optionFailed = question.failed[option.key];
									incrementFailureCount(optionFailed);
								}
							} else {
								if (question.answer && question.answer[option.key] === false) {
									const optionFailed = question.failed[option.key];
									incrementFailureCount(optionFailed);
								}
							}
						}
					}
				} else if (question.type === 'ta-radio') {
					if (question.failed) {
						incrementFailureCount(question.failed);
					}
				}

				if (question.answer) {
					if (question.answer === 'option_0') {
						audit.scores.push(6);
					} else if (question.answer === 'option_1') {
						audit.scores.push(-1);
					} else if (question.answer === 'option_2') {
						audit.scores.push(2);
					}
				}

				section.questions.push(question);
			};

			const checkNestedQuestionFields = async (questionsWithHideExpressions: IFormConfigField[], field: IFormConfigField, length: number) => {
				const updateNestedQuestionField = async (answer: any) => {
					if (answer) {
						const response = answer.replace('option_', 'response_');
						const path = field.key.split('.value')[0];
						const partialNestedKey = path + '.' + response + '.nested_question';
						const nestedQuestionFields = questionsWithHideExpressions.filter(n => n.key.includes(partialNestedKey) && (n.key.match(/nested_question/g) || []).length === length);
						if (nestedQuestionFields.length > 0) {
							for (const nestedQuestionField of nestedQuestionFields) {
								await generateQuestionField(nestedQuestionField);
								await checkNestedQuestionFields(questionsWithHideExpressions, nestedQuestionField, length + 1);
							}
						}
					}
				};

				const questionAnswer = get(this.formlyService.getModel(), field.key, null);
				if (field.type === 'ta-radio' || field.type === 'ta-pass-fail' || field.type === 'ta-pass-fail-na') {
					await updateNestedQuestionField(questionAnswer);
				} else if (field.type === 'ta-checkbox') {
					if (questionAnswer) {
						const checkboxAnswers = Object.keys(questionAnswer).filter(v => questionAnswer[v]);
						for (const checkboxAnswer of checkboxAnswers) {
							await updateNestedQuestionField(checkboxAnswer);
						}
					}
				}
			};

			// Loop through all of the questions in the section
			let questionNumber = 0;
			for (const formConfigQuestion of formConfigSection.questions) {
				const questionsWithHideExpressions = formConfigQuestion.fields.filter(nq => (nq.hideExpression));
				questionNumber++;
				for (const field of formConfigQuestion.fields) {
					/*
					  In our formConfig, additional photo and comments are treated as separate Formly fields. Thus, a question has
					  a key that ends with '.value' and the key for additional fields end with '.comments' or '.photos'. However,
					  logically photos/comments should belong to its parent question alongside the answer. Therefore, the following
					  boolean creates a question object only if a true question is encountered i.e. the key ends with '.value'.
					  Any additional fields for the question are then retrieved and stored inside the question object.
					*/
					if (field.key.endsWith('.value')) {
						/*
						Only add fields/questions that have no hide expression i.e. the question *always* shows, or questions that
						have a hide expression but have been triggered to show by the user
						*/
						if (!field.hideExpression) {
							await generateQuestionField(field);
							// With nested questions, check if it has been shown by the user. If shown, generate the question object
							if (questionsWithHideExpressions.length > 0) {
								await checkNestedQuestionFields(questionsWithHideExpressions, field, 1);
							}
						}
					}
				}
			}
		};

		// Loop through all of the sections in the form config
		for (const formConfigSection of formConfig.sections) {
			const section: any = {};
			section.title = formConfigSection.title;
			section.uuid = formConfigSection.uuid;
			section.questions = [];

			// Section failure status
			section.failures = {
				total: 0,
				category_a_count: 0,
				category_b_count: 0,
				category_c_count: 0
			};

			await updateQuestions(formConfigSection, section);
			audit.sections.push(section);
		}

		delete audit.data;

		this.submittedAudit = audit;
	}

	public async saveAudit() {
		try {
			// await this.databaseService.saveDocument(this.formlyService.getModel().id, this.formlyService.getModel());
		} catch (error) {
			throw error;
		}
	}

}
